import * as React from "react";
import {
  Table,
  Link,
  SpaceBetween,
  Header,
  CopyToClipboard,
  Button,
  Spinner,
  Badge,
} from "@amzn/awsui-components-console";
import { useJemGen } from "src/context/jem-gen/jemGenContext";
import { InitDomain } from "src/context/domain/domainContext";
import {
  BillingModeType,
  ConfigEnvironment,
  FusePlan,
} from "@amzn/fuse-accounting-central-datastore-client";
import { AccountingRegistrationModal } from "./accounting-registration-modal";
import { LinkRegistrationModal } from "./link-registration-modal";
import { SPLIT_PANEL_STICKY_HEADER_OFFSET } from "./split-panel.helper";
import { getBartPortalUrl, RegionalDomain } from "src/config/BartLambdaConfig";
import { getFusePortalPlanUrl } from "src/config/fuse-portal-config";
import { getFixedFeeDealStatus } from "src/components/allFixedFeeConfigsPage/fixed-fee-config-attributes";

interface AssociatedPlansProps {}

export function AssociatedPlans(props: AssociatedPlansProps) {
  const {
    selectedArdDetailed,
    ardRegionMap,
    fetchArdData,
    registrationsLoadingState,
    availableRegistrationId,
    fixedFeeConfigsByPlanId,
    fixedFeeConfigsLoadingState,
    refreshFixedFeeConfigs,
  } = useJemGen();
  const [showRegistrationModal, setShowRegistrationModal] =
    React.useState(false);
  const [selectedRegistrationId, setSelectedRegistrationId] = React.useState<
    string | null
  >(null);
  const [registrationModalMode, setRegistrationModalMode] = React.useState<
    "view" | "create"
  >("view");

  // State for link registration modal
  const [showLinkModal, setShowLinkModal] = React.useState(false);
  const [selectedPlanForLinking, setSelectedPlanForLinking] =
    React.useState<FusePlan | null>(null);
  const [registrationIdToLink, setRegistrationIdToLink] = React.useState<
    string | null
  >(null);

  const { authService } = React.useContext(InitDomain);
  const canEdit = authService.ldap.includes("bart-admin");

  const selectedArd = selectedArdDetailed?.ard;

  // Map region to the matching ORCA Studio domain
  const getOrcaDomainForRegion = (region: string) => {
    const orcaDomainMap: { [key: string]: string } = {
      "devo-FE": "beta.us-west-2",
      "prod-FE": "us-west-2",
      "prod-US": "us-east-1",
      "prod-EU": "eu-south-2",
    };

    return orcaDomainMap[region] || "beta.us-west-2";
  };

  // Get region for the selected ARD
  const getRegionForArd = (ardId: string): RegionalDomain => {
    return (ardRegionMap?.get(ardId) || "devo-FE") as RegionalDomain;
  };

  const associatedPlans = selectedArd?.associatedPlans || [];

  const isFixedFee = selectedArd?.billingMode === BillingModeType.FIXED_FEE;

  // FixedFeeConfigs are preloaded once at the page level (see JemGenContext)
  // via ListFixedFeeConfigs across all regions/environments, rather than via
  // per-plan GetFixedFeeConfigByFusePlanId calls whose API alarm threshold is
  // too low for the volume of plans on an ARD. We just filter the cached map.
  const loadingFixedFeeConfigs = fixedFeeConfigsLoadingState !== "loaded";

  // Render the consolidated FixedFeeConfig cell for a plan. Both PROD and TEST
  // configs are listed together, each row carrying an inline environment tag
  // (PROD/TEST) alongside the full fixed fee ID and deal status badge.
  const renderFixedFeeConfigCell = (plan: FusePlan) => {
    if (!plan.id) {
      return "N/A";
    }
    const configs = (fixedFeeConfigsByPlanId.get(plan.id) ?? []).filter(
      (config) => config.fixedFeeId,
    );
    if (configs.length === 0) {
      // No config yet — show a spinner only while the page-level preload is
      // still running, otherwise there genuinely is none.
      return loadingFixedFeeConfigs ? <Spinner /> : "N/A";
    }
    const region = getRegionForArd(selectedArd?.id || "");
    return (
      <SpaceBetween direction="vertical" size="xs">
        {configs.map((config) => {
          const dealStatus = getFixedFeeDealStatus(config);
          const isProd = config.environment === ConfigEnvironment.PRODUCTION;
          return (
            // Inline flex (not horizontal SpaceBetween, which wraps in narrow
            // columns) keeps the badges on the same line as the link. Badges
            // must not shrink, else their text wraps one letter per line.
            <div
              key={config.fixedFeeId}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                whiteSpace: "nowrap",
              }}
            >
              <span style={{ flexShrink: 0 }}>
                <Badge color={isProd ? "green" : "blue"}>
                  {isProd ? "PROD" : "TEST"}
                </Badge>
              </span>
              <Link
                external
                href={getBartPortalUrl(
                  region,
                  `/getfixedfeeconfig/${config.fixedFeeId}`,
                )}
                ariaLabel={config.fixedFeeId}
              >
                {config.fixedFeeId}
              </Link>
              <span style={{ flexShrink: 0 }}>
                <Badge color={dealStatus.color}>{dealStatus.content}</Badge>
              </span>
            </div>
          );
        })}
      </SpaceBetween>
    );
  };

  // Generate external links for a plan
  const generateExternalLinks = (plan: FusePlan) => {
    if (!selectedArd?.id) return null;

    const region = getRegionForArd(selectedArd.id);
    const orcaDomain = getOrcaDomainForRegion(region);

    const fusePortalUrl = getFusePortalPlanUrl(region, plan.id ?? "");
    const bartUrl = getBartPortalUrl(region, `/getplan/${plan.id}`);
    const orcaUrl = `https://${orcaDomain}.studio.orca.amazon.dev/#/clients/fuse-billing/executions/actions/query?filters=Tags+%253D+plan%253A${plan.id}`;

    return (
      <SpaceBetween direction="horizontal" size="s">
        <Link external href={fusePortalUrl}>
          Fuse Portal
        </Link>
        <Link external href={bartUrl}>
          Bart
        </Link>
        <Link external href={orcaUrl}>
          WF Samples
        </Link>
      </SpaceBetween>
    );
  };

  const handleRegistrationClick = React.useCallback(
    (registrationId: string) => {
      setSelectedRegistrationId(registrationId);
      setRegistrationModalMode("view");
      setShowRegistrationModal(true);
    },
    [],
  );

  const handleCreateRegistrationClick = React.useCallback(() => {
    setSelectedRegistrationId(null);
    setRegistrationModalMode("create");
    setShowRegistrationModal(true);
  }, []);

  const handleModalDismiss = React.useCallback(() => {
    setShowRegistrationModal(false);
    setSelectedRegistrationId(null);
    setRegistrationModalMode("view");
  }, []);

  const handleCreateSuccess = React.useCallback(() => {
    console.log("Registration created successfully");
    if (selectedArd?.id) {
      fetchArdData(selectedArd.id);
    }
  }, [selectedArd?.id, fetchArdData]);

  const handleLinkRegistrationClick = (
    plan: FusePlan,
    registrationId: string,
  ) => {
    setSelectedPlanForLinking(plan);
    setRegistrationIdToLink(registrationId);
    setShowLinkModal(true);
  };

  const handleLinkModalDismiss = () => {
    setShowLinkModal(false);
    setSelectedPlanForLinking(null);
    setRegistrationIdToLink(null);
  };

  const handleLinkSuccess = () => {
    console.log("Registration linked successfully");
    if (selectedArd?.id) {
      fetchArdData(selectedArd?.id);
    }
  };

  // Render the accounting registration ID cell
  const renderAccountingRegistrationCell = (plan: FusePlan) => {
    // If plan already has a registration ID, show it as a link
    if (plan.accountingRegistrationId) {
      return (
        <Button
          variant="inline-link"
          onClick={() =>
            handleRegistrationClick(plan.accountingRegistrationId!)
          }
        >
          {plan.accountingRegistrationId}
        </Button>
      );
    }

    // Plan doesn't have a registration ID - check if we can link one
    if (availableRegistrationId) {
      const regId = availableRegistrationId;
      return (
        <Button
          variant="inline-link"
          onClick={() => handleLinkRegistrationClick(plan, regId)}
        >
          Link Registration
        </Button>
      );
    }

    // No registration available to link - show create button (only for admins)
    if (!canEdit) {
      return "N/A";
    }

    return (
      <Button variant="inline-link" onClick={handleCreateRegistrationClick}>
        Create Registration
      </Button>
    );
  };

  const columnDefinitions = [
    {
      id: "id",
      header: "Plan ID",
      width: 350,
      cell: (plan: FusePlan) => {
        if (!plan.id) {
          return "N/A";
        }
        return (
          <CopyToClipboard
            copyButtonAriaLabel="Copy Plan ID"
            copyErrorText="Plan ID failed to copy"
            copySuccessText="Plan ID copied"
            textToCopy={plan.id}
            variant="inline"
          />
        );
      },
    },
    {
      id: "accountingRegistrationId",
      header: "Accounting Registration ID",
      minWidth: 320,
      cell: (plan: FusePlan) => renderAccountingRegistrationCell(plan),
    },
    // Only show the Fixed Fee Config column for FIXED_FEE ARDs. PROD and TEST
    // configs are consolidated into one column, each tagged inline.
    ...(isFixedFee
      ? [
          {
            id: "fixedFeeConfig",
            header: "Fixed Fee Config",
            minWidth: 540,
            cell: (plan: FusePlan) => renderFixedFeeConfigCell(plan),
          },
        ]
      : []),
    {
      id: "fusenEnabled",
      header: "Fusen Enabled",
      width: 100,
      cell: (plan: FusePlan) => (plan.fusenEnabled ? "True" : "False"),
    },
    {
      id: "migrationTargetStage",
      header: "Migration Target Stage",
      width: 120,
      cell: (plan: FusePlan) => plan.migrationTargetStage || "N/A",
    },
    {
      id: "glLineDescriptionPartnerName",
      header: "GL Line Description Partner Name",
      width: 350,
      cell: (plan: FusePlan) => plan.glLineDescriptionPartnerName || "N/A",
    },
    {
      id: "fusenEnableDate",
      header: "Fusen Enable Date",
      width: 180,
      cell: (plan: FusePlan) => plan.fusenEnableDate || "N/A",
    },
    {
      id: "externalLinks",
      header: "External Links",
      minWidth: 300,
      cell: (plan: FusePlan) => generateExternalLinks(plan),
    },
  ];

  if (!selectedArd) {
    return <>No ARD selected</>;
  }

  return (
    <>
      <Table
        columnDefinitions={columnDefinitions}
        items={associatedPlans}
        loadingText="Loading associated plans"
        loading={registrationsLoadingState === "loading"}
        sortingDisabled={true}
        stickyHeader
        stickyHeaderVerticalOffset={SPLIT_PANEL_STICKY_HEADER_OFFSET}
        stickyColumns={{ first: 1, last: 1 }}
        empty={
          <SpaceBetween size="m">
            <div>No associated plans found</div>
          </SpaceBetween>
        }
        header={
          <Header
            variant="h3"
            counter={`(${associatedPlans.length})`}
            actions={
              isFixedFee ? (
                <Button
                  iconName="refresh"
                  loading={loadingFixedFeeConfigs}
                  onClick={() => refreshFixedFeeConfigs()}
                >
                  Refresh Fixed Fee Configs
                </Button>
              ) : undefined
            }
          >
            Associated Plans
          </Header>
        }
      />

      <AccountingRegistrationModal
        visible={showRegistrationModal}
        accountingRegistrationId={selectedRegistrationId}
        onDismiss={handleModalDismiss}
        mode={registrationModalMode}
        ardForCreate={
          registrationModalMode === "create" ? selectedArd : undefined
        }
        allPlans={
          registrationModalMode === "create" ? associatedPlans : undefined
        }
        onCreateSuccess={handleCreateSuccess}
      />

      <LinkRegistrationModal
        visible={showLinkModal}
        plan={selectedPlanForLinking}
        registrationId={registrationIdToLink}
        onDismiss={handleLinkModalDismiss}
        onSuccess={handleLinkSuccess}
      />
    </>
  );
}
